import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Container, Typography } from "@mui/material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { tokens } from "../theme";
import { Headline, Kicker, pillSx } from "../components/ui";
import Glyph from "../components/Glyph";
import EmailCapture from "../components/EmailCapture";
import ScrollPopup from "../components/ScrollPopup";
import { useCart } from "../components/CartContext";
import { FALLBACK_PRODUCTS } from "../data/biomarkers";
import { FALLBACK_TESTING, money } from "../data/site";
import { getProducts, getTestingOptions } from "../services/veysoApi";
import { useApiData } from "../hooks/useApiData";
import { CARE, CLOSING, EXPERTS, HERO, HOME_FAQS, PRODUCT_TAGS, SHOP_VALUES, STANDARD, TEST_ICONS, TRUST, YOU } from "../data/home";

const asset = (p) => `${process.env.PUBLIC_URL || ""}${p}`;

/** Centered content column: 1200 wide on desktop, with side padding on smaller screens. */
function Wrap({ children, sx }) {
  return <Container maxWidth={false} sx={{ maxWidth: 1200, px: { xs: 2.5, sm: 6, lg: 0 }, ...sx }}>{children}</Container>;
}

function Band({ bg, children, sx }) {
  return <Box component="section" sx={{ background: bg, py: { xs: 8, md: 12.5 }, ...sx }}>{children}</Box>;
}

const grid = (cols) => ({ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", sm: cols.sm || "repeat(2, 1fr)", md: cols.md } });

function ArrowButton({ to, sx, children }) {
  return <Button component={RouterLink} to={to} endIcon={<ArrowOutwardIcon sx={{ fontSize: "17px !important" }} />} sx={sx}>{children}</Button>;
}

/* ---------- Hero ---------- */
function Hero() {
  const [reduce, setReduce] = useState(false);
  const [videoOk, setVideoOk] = useState(true);
  useEffect(() => {
    const m = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)");
    if (m) setReduce(m.matches);
  }, []);
  return (
    <Box component="section" id="home-hero" sx={{ position: "relative", color: "#fff", minHeight: { xs: 640, sm: 820, md: 820 }, display: "flex", alignItems: "flex-end", overflow: "hidden",
      background: `url(${asset(HERO.poster)}) center top / cover, linear-gradient(180deg,#C7A47F 0%,#7A5C43 38%,#2F2A21 100%)` }}>
      {videoOk && (
        <Box component="video" autoPlay={!reduce} muted loop playsInline preload="metadata" poster={asset(HERO.poster)} onError={() => setVideoOk(false)}
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: { xs: "70% top", md: "center top" } }}>
          <source src={asset(HERO.video)} type="video/mp4" onError={() => setVideoOk(false)} />
        </Box>
      )}
      <Box aria-hidden sx={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(30,26,18,.55) 0%, rgba(30,26,18,0) 55%), linear-gradient(0deg, rgba(30,26,18,.7) 0%, rgba(30,26,18,0) 45%)" }} />
      <Wrap sx={{ position: "relative", pb: { xs: 6, md: 11 }, width: "100%", maxWidth: { lg: 1312 } }}>
        <Typography sx={{ fontSize: 13, fontWeight: 500, letterSpacing: ".12em", textTransform: "uppercase", mb: 2.5 }}>{HERO.eyebrow}</Typography>
        <Typography variant="h1" sx={{ fontWeight: 300, fontSize: { xs: 44, sm: 64, md: 80 }, lineHeight: 1.04, letterSpacing: "-0.02em", maxWidth: { xs: 360, sm: 700, md: 560 } }}>{HERO.title}</Typography>
        <Typography sx={{ mt: 3, fontSize: { xs: 17, md: 20 }, fontWeight: 300, opacity: 0.95 }}>{HERO.subtitle}</Typography>
        <Box sx={{ mt: 4.5, display: "flex", gap: 1.5, flexWrap: "wrap", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" } }}>
          <ArrowButton to="/shop" sx={pillSx.oxide}>Shop supplements</ArrowButton>
          <ArrowButton to="/telehealth" sx={pillSx.ghostLight}>Telehealth</ArrowButton>
        </Box>
      </Wrap>
    </Box>
  );
}

/* ---------- Trust strip ---------- */
function TrustStrip() {
  const row = TRUST.map(([g, label]) => (
    <Box key={label} sx={{ display: "inline-flex", alignItems: "center", gap: 1.25, mr: 7, whiteSpace: "nowrap" }}>
      <Glyph name={g} sx={{ fontSize: 20, opacity: 0.85 }} />
      <Typography sx={{ fontSize: 15, opacity: 0.92 }}>{label}</Typography>
    </Box>
  ));
  return (
    <Box aria-label="Trust and quality" sx={{ background: tokens.forestDark, color: "#fff", overflow: "hidden", py: 2.4 }}>
      <Box sx={{ display: "flex", width: "max-content", animation: "veysoMarquee 40s linear infinite" }}>
        <Box sx={{ display: "flex" }}>{row}</Box>
        <Box aria-hidden sx={{ display: "flex" }}>{row}</Box>
      </Box>
    </Box>
  );
}

/* ---------- Shop ---------- */
function Placeholder({ label, sx }) {
  return (
    <Box sx={{ background: tokens.sand, display: "grid", placeItems: "center", textAlign: "center", color: tokens.muted, ...sx }}>
      <Box><ImageOutlinedIcon sx={{ fontSize: 22, opacity: 0.8 }} /><Typography sx={{ fontSize: 13 }}>{label}</Typography></Box>
    </Box>
  );
}

function ProductCard({ p, tag, onAdd }) {
  return (
    <Box sx={{ background: "#F3EEE6", borderRadius: "28px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Placeholder label={`Product photo · ${p.name}`} sx={{ height: 340, background: "#E8E0D4", borderRadius: "28px 28px 0 0" }} />
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", flex: 1 }}>
        <Typography sx={{ fontSize: 12, letterSpacing: ".12em", textTransform: "uppercase", color: tokens.muted }}>{tag}</Typography>
        <Typography component={RouterLink} to={`/shop/p/${p.id}`} sx={{ fontSize: 24, fontWeight: 400, color: tokens.ink, textDecoration: "none", mt: 1 }}>{p.name}</Typography>
        <Box sx={{ display: "flex", color: tokens.oxide, mt: 1 }}>{[0, 1, 2, 3, 4].map((i) => <StarIcon key={i} sx={{ fontSize: 15 }} />)}</Box>
        <Typography sx={{ color: tokens.muted, fontSize: 15, mt: 1, flex: 1 }}>{p.detail}</Typography>
        <Typography sx={{ fontSize: 20, mt: 2 }}>{money(p.priceCents)}</Typography>
        <Button fullWidth startIcon={<AddIcon />} onClick={onAdd} sx={{ ...pillSx.moss, mt: 2, py: 1.1, fontSize: 15 }}>Add to cart</Button>
        <Typography component={RouterLink} to={`/shop/p/${p.id}`} sx={{ mt: 2, fontSize: 14, color: tokens.mossBtn, textDecoration: "underline", display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          Learn more <ArrowOutwardIcon sx={{ fontSize: 14 }} />
        </Typography>
      </Box>
    </Box>
  );
}

function ShopSection() {
  const { add } = useCart();
  const { data } = useApiData(() => getProducts("ADD_ON"), FALLBACK_PRODUCTS.filter((p) => p.type === "ADD_ON"));
  const products = (data || []).slice(0, 3);
  return (
    <Band bg={tokens.cream}>
      <Wrap>
        <Kicker>Shop</Kicker>
        <Headline top="Better products." em="Higher standards." />
        <Typography sx={{ mt: 3, fontSize: 20, fontWeight: 300, color: tokens.muted, maxWidth: 520 }}>Discover carefully selected wellness products backed by research and expert insight.</Typography>
        <Typography sx={{ mt: 7, mb: 2.5, fontSize: 20 }}>Featured products</Typography>
        <Box sx={grid({ md: "repeat(3, 1fr)" })}>
          {products.map((p, i) => <ProductCard key={p.id} p={p} tag={PRODUCT_TAGS[i % PRODUCT_TAGS.length]} onAdd={() => add({ id: p.id, name: p.name, price: p.priceCents / 100 })} />)}
        </Box>
        <ArrowButton to="/shop" sx={{ ...pillSx.moss, mt: 5 }}>Shop all</ArrowButton>
        <Box sx={{ mt: 8, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, maxWidth: 840, gap: 3 }}>
          {SHOP_VALUES.map(([g, label], i) => (
            <Box key={label} sx={{ textAlign: "center", borderLeft: { sm: i ? `1px solid ${tokens.line}` : 0 }, px: 2 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: "50%", background: tokens.sand, display: "grid", placeItems: "center", mx: "auto", mb: 1.5 }}><Glyph name={g} sx={{ color: tokens.mossBtn }} /></Box>
              <Typography sx={{ fontSize: 15.5, maxWidth: 140, mx: "auto" }}>{label}</Typography>
            </Box>
          ))}
        </Box>
      </Wrap>
    </Band>
  );
}

/* ---------- Testing ---------- */
function TestingSection() {
  const { data } = useApiData(getTestingOptions, FALLBACK_TESTING);
  const tests = (data || []).slice(0, 4);
  return (
    <Band bg={tokens.sand}>
      <Wrap>
        <Kicker>Testing</Kicker>
        <Headline top="Comprehensive" em="biomarkers." />
        <Typography sx={{ mt: 3, fontSize: 20, fontWeight: 300, color: tokens.muted, maxWidth: 620 }}>Choose from at-home testing options to get a clearer picture of your health.</Typography>
        <Box sx={{ ...grid({ md: "repeat(4, 1fr)" }), mt: 7 }}>
          {tests.map((t) => (
            <Box key={t.slug} sx={{ background: tokens.cream, borderRadius: "28px", p: 3.5, display: "flex", flexDirection: "column", minHeight: 435 }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", background: tokens.sand, display: "grid", placeItems: "center" }}><Glyph name={TEST_ICONS[t.slug] || "drop"} sx={{ color: tokens.mossBtn, fontSize: 28 }} /></Box>
              <Typography sx={{ fontSize: 24, mt: 3 }}>{t.name}</Typography>
              <Typography sx={{ color: tokens.muted, fontSize: 16, mt: 1.5, flex: 1 }}>{t.detail || t.tagline}</Typography>
              <Typography sx={{ mt: 3, fontSize: 17 }}>From {money(t.fromCents)}</Typography>
              <Button component={RouterLink} to={`/testing/${t.slug}`} fullWidth sx={{ ...pillSx.moss, mt: 2, py: 1.1, fontSize: 15 }}>View test</Button>
            </Box>
          ))}
        </Box>
        <ArrowButton to="/testing" sx={{ ...pillSx.moss, mt: 5 }}>Shop all tests</ArrowButton>
      </Wrap>
    </Band>
  );
}

/* ---------- Experts ---------- */
function ExpertsSection() {
  return (
    <Band bg={tokens.cream}>
      <Wrap>
        <Kicker color={tokens.oxideHover}>Research-led · Expert-informed</Kicker>
        <Headline top="Built on science." em="Backed by experts." />
        <Typography sx={{ mt: 3, fontSize: 20, fontWeight: 300, color: tokens.muted, maxWidth: 620 }}>Clinicians, researchers, and educators who help guide what we test, recommend, and sell.</Typography>
        <Box sx={{ ...grid({ md: "repeat(3, 1fr)" }), mt: 7 }}>
          {EXPERTS.map((e) => (
            <Box key={e.name}>
              {e.photo
                ? <Box component="img" src={asset(e.photo)} alt={e.name} sx={{ width: "100%", height: 560, objectFit: "cover", borderRadius: "28px", display: "block" }} />
                : <Placeholder label="Portrait photo" sx={{ height: { xs: 420, md: 560 }, borderRadius: "28px", border: "1px dashed #CDBBA3", background: "#F0E8DD" }} />}
              <Typography sx={{ mt: 2.5, fontSize: 19 }}>{e.name}</Typography>
              <Typography sx={{ color: tokens.muted, fontSize: 15.5 }}>{e.role}</Typography>
            </Box>
          ))}
        </Box>
      </Wrap>
    </Band>
  );
}

/* ---------- Standard + member story ---------- */
function StandardSection() {
  const s = STANDARD;
  return (
    <Box component="section" sx={{ background: tokens.brown, color: "#fff", pt: { xs: 9, md: 12 }, pb: { xs: 16, md: 20 }, textAlign: "center" }}>
      <Wrap>
        <Typography sx={{ fontSize: 12.5, letterSpacing: ".14em", textTransform: "uppercase", opacity: 0.85 }}>{s.kicker}</Typography>
        <Box sx={{ width: 48, borderTop: "1px solid rgba(255,255,255,.4)", mx: "auto", mt: 2.5, mb: 5 }} />
        <Typography variant="h2" sx={{ fontSize: { xs: 40, md: 56 }, fontWeight: 400, lineHeight: 1.1, maxWidth: 560, mx: "auto" }}>{s.title}</Typography>
        <Typography sx={{ mt: 3, fontSize: 20, fontStyle: "italic", fontWeight: 300, opacity: 0.85 }}>{s.sub}</Typography>
        <Box sx={{ mt: 6, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, maxWidth: 720, mx: "auto", gap: 3 }}>
          {s.pillars.map(([g, t, d], i) => (
            <Box key={t} sx={{ borderLeft: { sm: i ? "1px solid rgba(255,255,255,.2)" : 0 }, px: 2 }}>
              <Glyph name={g} sx={{ fontSize: 24 }} />
              <Typography sx={{ mt: 1.5, fontSize: 17 }}>{t}</Typography>
              <Typography sx={{ mt: 1, fontSize: 13.5, opacity: 0.75 }}>{d}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ mt: 7, mx: "auto", maxWidth: 840, background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.14)", borderRadius: "32px", p: { xs: 3, md: 5 } }}>
          <Typography sx={{ fontSize: 12.5, letterSpacing: ".14em", textTransform: "uppercase", opacity: 0.8 }}>{s.story.kicker}</Typography>
          <Typography sx={{ mt: 2.5, fontSize: { xs: 20, md: 24 }, fontStyle: "italic", fontWeight: 300, lineHeight: 1.4 }}>{s.story.quote}</Typography>
          <Box sx={{ mt: 3, display: "inline-flex", alignItems: "center", gap: 1.5, textAlign: "left" }}>
            <Box sx={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#C7A47F,#606E52)", display: "grid", placeItems: "center", fontSize: 20 }}>{s.story.name[0]}</Box>
            <Box><Typography sx={{ fontSize: 17 }}>{s.story.name}</Typography><Typography sx={{ fontSize: 13.5, opacity: 0.8 }}>{s.story.role}</Typography></Box>
          </Box>
        </Box>
        <Typography component={RouterLink} to="/about" sx={{ mt: 4, display: "inline-flex", alignItems: "center", gap: 0.75, color: "#fff", fontSize: 16, textDecoration: "underline" }}>
          View our standard <ArrowOutwardIcon sx={{ fontSize: 15 }} />
        </Typography>
      </Wrap>
    </Box>
  );
}

/* ---------- Care + VEYSO YOU (one sand section with rounded top over the brown band) ---------- */
function CareCard({ c, tint, accent, shape, btn }) {
  return (
    <Box sx={{ position: "relative", overflow: "hidden", background: tint, borderRadius: "32px", p: { xs: 3, md: 5 }, display: "flex", flexDirection: "column" }}>
      <Box aria-hidden sx={{ position: "absolute", right: -80, bottom: -140, width: 520, height: 360, borderRadius: "50%", background: shape }} />
      <Box sx={{ position: "relative", display: "flex", flexDirection: "column", flex: 1 }}>
        <Typography sx={{ fontSize: 12.5, letterSpacing: ".12em", textTransform: "uppercase", color: accent }}>{c.kicker}</Typography>
        <Typography sx={{ fontSize: { xs: 24, md: 28 }, mt: 2.5, fontWeight: 400 }}>{c.title}</Typography>
        <Typography sx={{ color: tokens.muted, mt: 1.5, fontSize: 16 }}>{c.text}</Typography>
        <Box sx={{ borderTop: "1px solid #DCCFBF", my: 3 }} />
        {c.perks.map((p) => (
          <Box key={p} sx={{ display: "flex", gap: 1.5, py: 0.9, alignItems: "center" }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 20, color: accent }} /><Typography sx={{ fontSize: 16 }}>{p}</Typography>
          </Box>
        ))}
        <Box sx={{ flex: 1, minHeight: 56 }} />
        <Typography sx={{ fontSize: 48, fontWeight: 300, lineHeight: 1 }}>{c.price}<Typography component="span" sx={{ fontSize: 16, color: tokens.muted }}> / month</Typography></Typography>
        <ArrowButton to={c.to} sx={{ ...btn, mt: 2.5, py: 1.5 }}>{c.cta}</ArrowButton>
      </Box>
    </Box>
  );
}

function CareAndYou() {
  return (
    <Box component="section" sx={{ background: tokens.sand, borderRadius: { xs: "40px 40px 0 0", md: "64px 64px 0 0" }, mt: { xs: -8, md: -10 }, position: "relative", pt: { xs: 8, md: 12.5 }, pb: { xs: 9, md: 13 } }}>
      <Wrap>
        <Kicker>Care</Kicker>
        <Headline top="Personalized care," em="on your terms." />
        <Typography sx={{ mt: 3, fontSize: 20, fontWeight: 300, color: tokens.muted, maxWidth: 620 }}>Access expert providers and evidence-based treatments to support your long-term health.</Typography>
        <Box sx={{ ...grid({ sm: "1fr", md: "repeat(2, 1fr)" }), mt: 6 }}>
          <CareCard c={CARE.member} tint="#ECEDE2" accent={tokens.mossBtn} shape="#DFE3D2" btn={{ ...pillSx.moss, justifyContent: "center" }} />
          <CareCard c={CARE.plus} tint="#F5E9E0" accent={tokens.oxide} shape="#EFD9CD" btn={{ ...pillSx.oxide, justifyContent: "center" }} />
        </Box>
        <Box sx={{ mt: 8, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 3 }}>
          {CARE.values.map(([g, label], i) => (
            <Box key={label} sx={{ textAlign: "center", borderLeft: { sm: i ? "1px solid #DCCFBF" : 0 } }}>
              <Box sx={{ width: 52, height: 52, borderRadius: "50%", background: tokens.cream, display: "grid", placeItems: "center", mx: "auto", mb: 1.5 }}><Glyph name={g} sx={{ color: tokens.mossBtn }} /></Box>
              <Typography sx={{ fontSize: 15.5 }}>{label}</Typography>
            </Box>
          ))}
        </Box>
        <ArrowButton to="/membership" sx={{ ...pillSx.moss, mt: 5 }}>Join VEYSO</ArrowButton>

        <Box sx={{ textAlign: "center", mt: { xs: 10, md: 16 } }}>
          <Kicker center>{YOU.kicker}</Kicker>
          <Typography variant="h2" sx={{ fontSize: { xs: 38, md: 56 }, lineHeight: 1.1, fontWeight: 300 }}>
            {YOU.title}<br /><Box component="span" sx={{ color: tokens.oxide }}>{YOU.em}</Box>
          </Typography>
          <Typography sx={{ mt: 3, fontSize: 20, fontWeight: 300, color: tokens.muted, maxWidth: 520, mx: "auto" }}>{YOU.text}</Typography>
        </Box>
        <Box sx={{ mt: 7, mx: "auto", maxWidth: 960, background: tokens.cream, borderRadius: "40px", boxShadow: "0 12px 40px rgba(58,40,30,.08)", p: { xs: 3, md: 6 }, display: "grid", gap: 4, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, alignItems: "center" }}>
          <Box>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, background: "#DFE6CF", borderRadius: 999, px: 1.75, py: 0.6, fontSize: 11.5, letterSpacing: ".12em", textTransform: "uppercase" }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: tokens.oxide }} />{YOU.card.pill}
            </Box>
            <Typography sx={{ fontSize: 26, mt: 2 }}>{YOU.card.title}</Typography>
            <Typography sx={{ color: tokens.muted, fontSize: 15.5, mt: 1 }}>{YOU.card.text}</Typography>
            {YOU.card.items.map(([g, t]) => (
              <Box key={t} sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.5 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: "50%", background: tokens.sand, display: "grid", placeItems: "center" }}><Glyph name={g} sx={{ fontSize: 17, color: tokens.mossBtn }} /></Box>
                <Typography sx={{ fontSize: 16 }}>{t}</Typography>
              </Box>
            ))}
          </Box>
          <Box>
            <EmailCapture note="No spam. Unsubscribe anytime." message="Please send me invites to VEYSO webinars." />
          </Box>
        </Box>
      </Wrap>
    </Box>
  );
}

/* ---------- FAQ ---------- */
function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <Band bg={tokens.cream} sx={{ py: { xs: 8, md: 12 } }}>
      <Wrap sx={{ display: "grid", gap: { xs: 5, md: 10 }, gridTemplateColumns: { xs: "1fr", md: "380px 1fr" } }}>
        <Box>
          <Kicker color={tokens.oxideHover}>Questions</Kicker>
          <Typography variant="h2" sx={{ fontSize: { xs: 40, md: 56 }, fontWeight: 300, lineHeight: 1.1 }}>Ask us anything.</Typography>
          <Typography sx={{ mt: 3, fontSize: 18, fontWeight: 300, color: tokens.muted }}>Straight answers from our team. Can’t find yours? We’re here to help.</Typography>
        </Box>
        <Box>
          {HOME_FAQS.map((f, i) => (
            <Box key={f.q} sx={{ borderTop: "1px solid #D9B8A5", ...(i === HOME_FAQS.length - 1 ? { borderBottom: "1px solid #D9B8A5" } : {}) }}>
              <Box component="button" type="button" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i}
                sx={{ all: "unset", boxSizing: "border-box", width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", py: 2.75, cursor: "pointer", fontSize: 18, fontFamily: "inherit", color: tokens.ink }}>
                {f.q}{open === i ? <RemoveIcon /> : <AddIcon />}
              </Box>
              {open === i && <Typography sx={{ color: tokens.muted, fontSize: 15.5, pb: 3, maxWidth: 680 }}>{f.a}</Typography>}
            </Box>
          ))}
          <Typography component={RouterLink} to="/faqs" sx={{ mt: 4, display: "inline-flex", alignItems: "center", gap: 0.5, color: tokens.ink, fontSize: 15, textDecoration: "underline" }}>
            See all questions <ArrowOutwardIcon sx={{ fontSize: 14 }} />
          </Typography>
        </Box>
      </Wrap>
    </Band>
  );
}

/* ---------- Closing band ---------- */
function Closing() {
  const [img, setImg] = useState(true);
  return (
    <Band bg={tokens.mossBtn} sx={{ color: "#fff", py: { xs: 8, md: 12 } }}>
      <Wrap sx={{ display: "grid", gap: 6, gridTemplateColumns: { xs: "1fr", md: "1fr 480px" }, alignItems: "center" }}>
        <Box>
          <Typography sx={{ fontSize: 12.5, letterSpacing: ".14em", textTransform: "uppercase", opacity: 0.85, mb: 3 }}>{CLOSING.kicker}</Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: 40, md: 56 }, fontWeight: 300, lineHeight: 1.1, maxWidth: 520 }}>{CLOSING.title}</Typography>
          <Box sx={{ mt: 4.5, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <ArrowButton to="/shop" sx={pillSx.cream}>Shop now</ArrowButton>
            <Button component={RouterLink} to="/membership" sx={pillSx.ghostLight}>Join VEYSO</Button>
          </Box>
        </Box>
        <Box sx={{ height: { xs: 260, md: 280 }, borderRadius: "28px", overflow: "hidden", background: "linear-gradient(135deg,#8A6A4C,#3A2E22 60%,#606E52)" }}>
          {img && <Box component="img" src={asset(CLOSING.image)} alt="" onError={() => setImg(false)} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
        </Box>
      </Wrap>
    </Band>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <ScrollPopup />
      <TrustStrip />
      <ShopSection />
      <TestingSection />
      <ExpertsSection />
      <StandardSection />
      <CareAndYou />
      <FaqSection />
      <Closing />
    </>
  );
}
